import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["__scopeDialogModal"],
  _excluded2 = ["__scopeDialogModal", "asChild"],
  _excluded3 = ["__scopeDialogModal", "asChild"],
  _excluded4 = ["__scopeDialogModal"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { Button } from '../../../button';
import { createDialogScope } from '../../../headless/dialog/context';
import { DomPrimitive } from '../../../internal/components/DomPrimitive';
import { Slot } from '../../../internal/components/Slot';
import { createContextScope } from '../../../internal/context/scoped-context';
import { useComposedRefs } from '../../../internal/hooks/useComposedRefs';
import { composeEventHandlers } from '../../../internal/utils/composeEventHandlers';
import { ModalBody, ModalClose, ModalCloseButton, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalRoot, ModalTitle, ModalTrigger } from '../Modal';
import * as styles from './DialogModal.module.css';
import { jsx as _jsx } from "react/jsx-runtime";
var ROOT_NAME = 'DialogModal';
var CONTENT_NAME = 'DialogModalContent';
var ACTIONS_NAME = 'DialogModalActions';
var ACTION_NAME = 'DialogModalAction';
var CANCEL_NAME = 'DialogModalCancel';

/**
 * создаем отдельный скоуп для DialogModal
 * чтобы избежать ошибок композиции контекстов с другими модальными окнами
 */

var _createContextScope = createContextScope(ROOT_NAME, [createDialogScope]),
  _createContextScope2 = _slicedToArray(_createContextScope, 2),
  createDialogModalContext = _createContextScope2[0],
  createDialogModalScope = _createContextScope2[1];
var useDialogScope = createDialogScope();
var _createDialogModalCon = createDialogModalContext(CONTENT_NAME),
  _createDialogModalCon2 = _slicedToArray(_createDialogModalCon, 2),
  DialogModalContentProvider = _createDialogModalCon2[0],
  useDialogModalContentContext = _createDialogModalCon2[1];

/**
 * HOC для оборачивания компонентов Modal в скоуп DialogModal
 * при необходимости расширения функциональности, можно создать отдельные компонеты, см DialogModalContent
 */
var withDialogModalScope = function withDialogModalScope(Component) {
  var WrappedComponent = function WrappedComponent(props) {
    var __scopeDialogModal = props.__scopeDialogModal,
      restProps = _objectWithoutProperties(props, _excluded);
    var modalScope = useDialogScope(__scopeDialogModal);
    return /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({}, modalScope), restProps));
  };
  WrappedComponent.displayName = "Alert(".concat(Component.displayName || Component.name, ")");
  return WrappedComponent;
};

/**
 * Копоненты Modal без изменения функционала, обернутые в скоуп DialogModal
 */
export var DialogModalRoot = withDialogModalScope(ModalRoot);
export var DialogModalTrigger = withDialogModalScope(ModalTrigger);
export var DialogModalClose = withDialogModalScope(ModalClose);
export var DialogModalCloseButton = withDialogModalScope(ModalCloseButton);
export var DialogModalHeader = withDialogModalScope(ModalHeader);
export var DialogModalTitle = withDialogModalScope(ModalTitle);
export var DialogModalBody = withDialogModalScope(ModalBody);
export var DialogModalDescription = withDialogModalScope(ModalDescription);
export var DialogModalFooter = withDialogModalScope(ModalFooter);
/**
 * Компонент обертка DialogModalActions
 * используется для правильного позиционирования кнопок действия
 * @example
 * <DialogModalActions>
 *  <DialogModalCancel>Отмена</DialogModalCancel>
 *  <DialogModalAction>Принять</DialogModalAction>
 * </DialogModalActions>
 */
export var DialogModalActions = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: styles['actions'],
    children: children
  });
});
DialogModalActions.displayName = ACTIONS_NAME;

/**
 * Button-like компонент для "позитивного" действия в модальном окне
 */

export var DialogModalAction = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var __scopeDialogModal = props.__scopeDialogModal,
    asChild = props.asChild,
    rest = _objectWithoutProperties(props, _excluded2);
  var modalScope = useDialogScope(__scopeDialogModal);
  var Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, modalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      size: "L"
    }, rest), {}, {
      ref: forwardedRef
    }))
  }));
});
DialogModalAction.displayName = ACTION_NAME;

/**
 * Button-like компонент для "негативного" действия в модальном окне
 */

export var DialogModalCancel = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var __scopeDialogModal = props.__scopeDialogModal,
    asChild = props.asChild,
    rest = _objectWithoutProperties(props, _excluded3);
  var _useDialogModalConten = useDialogModalContentContext(CANCEL_NAME, __scopeDialogModal),
    cancelRef = _useDialogModalConten.cancelRef;
  var modalScope = useDialogScope(__scopeDialogModal);
  var ref = useComposedRefs(forwardedRef, cancelRef);
  var Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, modalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      theme: "fill_secondary",
      size: "L"
    }, rest), {}, {
      ref: ref
    }))
  }));
});
DialogModalCancel.displayName = CANCEL_NAME;
export var DialogModalContent = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var __scopeDialogModal = props.__scopeDialogModal,
    contentProps = _objectWithoutProperties(props, _excluded4);
  var modalScope = useDialogScope(__scopeDialogModal);
  var cancelRef = React.useRef(null);
  /**
   * При открытии фокусируемся на кнопке закрытия по умолчанию
   */
  var handleOnOpenAutoFocus = React.useMemo(function () {
    return composeEventHandlers(contentProps.onOpenAutoFocus, function (event) {
      var _cancelRef$current;
      event.preventDefault();
      (_cancelRef$current = cancelRef.current) === null || _cancelRef$current === void 0 || _cancelRef$current.focus({
        preventScroll: true
      });
    });
  }, [contentProps.onOpenAutoFocus]);
  return /*#__PURE__*/_jsx(DialogModalContentProvider, {
    cancelRef: cancelRef,
    scope: __scopeDialogModal,
    children: /*#__PURE__*/_jsx(ModalContent, _objectSpread(_objectSpread(_objectSpread({}, modalScope), contentProps), {}, {
      role: "DialogModal",
      portalProps: modalScope,
      overlayProps: modalScope,
      onOpenAutoFocus: handleOnOpenAutoFocus,
      ref: forwardedRef
    }))
  });
});
DialogModalContent.displayName = CONTENT_NAME;
export { createDialogModalScope };